import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Mic, MicOff, Volume2, VolumeX, Sparkles, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Usar logo original de Mr Frogs
const FROGGY_LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_84eb74f9-05f0-495f-9ed7-6adf8a564e0e/artifacts/waed5a1p_LOGO%20FROGS.jpg";

// CSS animations para el movimiento
const animationStyles = `
@keyframes froggyDance {
  0%, 100% { transform: rotate(-5deg) scale(1); }
  25% { transform: rotate(5deg) scale(1.05); }
  50% { transform: rotate(-5deg) scale(1); }
  75% { transform: rotate(5deg) scale(1.05); }
}
@keyframes froggyWave {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-10deg); }
  75% { transform: rotate(10deg); }
}
@keyframes froggyJump {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-8px) scale(1.1); }
}
@keyframes froggyThink {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(3px); }
}
@keyframes froggySpeaking {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}
`;

// Animated Froggy Avatar with original logo
const FroggyAvatar = ({ pose = 'standing', isTyping, isSpeaking, size = 'normal', onPoseChange }) => {
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
    // Inject animation styles
    if (!document.getElementById('froggy-animations')) {
      const style = document.createElement('style');
      style.id = 'froggy-animations';
      style.textContent = animationStyles;
      document.head.appendChild(style);
    }
  }, []);
  
  const sizeClasses = {
    small: 'w-16 h-16',
    normal: 'w-20 h-20',
    large: 'w-24 h-24'
  };
  
  // Determinar animación según pose
  const getAnimation = () => {
    if (isSpeaking) return 'froggySpeaking 0.5s ease-in-out infinite';
    if (isTyping) return 'froggyThink 0.8s ease-in-out infinite';
    switch (pose) {
      case 'dancing': return 'froggyDance 0.6s ease-in-out infinite';
      case 'waving': return 'froggyWave 0.8s ease-in-out infinite';
      case 'jumping': return 'froggyJump 0.5s ease-in-out infinite';
      case 'thinking': return 'froggyThink 1s ease-in-out infinite';
      case 'walking': return 'froggyWave 1s ease-in-out infinite';
      default: return 'none';
    }
  };
  
  return (
    <div className="relative">
      {/* Círculo con el logo animado */}
      <div 
        className={`${sizeClasses[size]} rounded-full shadow-xl overflow-hidden border-[3px] border-green-500 ${
          isTyping ? 'ring-2 ring-yellow-400' : ''
        } ${isSpeaking ? 'ring-4 ring-green-300 ring-opacity-50' : ''}`}
        style={{ animation: getAnimation() }}
      >
        <img 
          src={FROGGY_LOGO}
          alt="Froggy"
          className="w-full h-full object-cover"
          style={{ objectPosition: 'center 25%' }}
        />
      </div>
      {/* Status indicator */}
      <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white shadow-md ${
        isTyping ? 'bg-yellow-400 animate-pulse' : 'bg-green-500'
      }`} />
      {/* Sparkles when dancing/celebrating */}
      {(pose === 'dancing' || pose === 'jumping') && (
        <div className="absolute -top-2 -right-2">
          <Sparkles className="w-5 h-5 text-yellow-400 animate-spin" />
        </div>
      )}
    </div>
  );
};

// Speech bubble that appears before chat opens
const SpeechBubble = ({ message, onClose }) => (
  <div className="absolute bottom-full right-0 mb-2 animate-in slide-in-from-bottom-2 duration-300">
    <div className="relative bg-white rounded-2xl shadow-xl p-3 max-w-[200px] border-2 border-green-400">
      <p className="text-sm text-gray-700 font-medium">{message}</p>
      {/* Triangle pointer */}
      <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r-2 border-b-2 border-green-400 transform rotate-45" />
    </div>
  </div>
);

// Main Froggy ELITE Animated Component
const FroggyEliteAnimated = ({ 
  customerName = null,
  customerPhone = null,
  currentProduct = null,
  cartTotal = 0,
  cartItems = [],
  onAddToCart = null 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [sessionId] = useState(() => `froggy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [voiceEnabled, setVoiceEnabled] = useState(true); // VOZ ACTIVADA POR DEFECTO
  const [isListening, setIsListening] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const [pose, setPose] = useState('standing');
  const [hasGreeted, setHasGreeted] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const [bubbleMessage, setBubbleMessage] = useState('');
  const [isEntering, setIsEntering] = useState(true);
  const [floatingPosition, setFloatingPosition] = useState({ x: 0, y: 0 });
  const [shortMessage, setShortMessage] = useState(''); // Mensaje corto visible
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);
  
  // Proactive messages for selling
  const proactiveMessages = [
    "¡Psst! ¿Ya viste las promos de hoy? 🔥",
    "¿Te ayudo a elegir algo delicioso? 🦐",
    "¡Los mariscos están fresquísimos hoy!",
    "¿Antojo de algo rico? ¡Pregúntame! 😋",
    "¡Hoy tenemos 2x1 en cocteles! 🍹"
  ];
  
  // Quick action buttons - focused on selling
  const quickActions = [
    { emoji: '🔥', text: 'Ofertas', query: '¿Cuáles son las ofertas de hoy?' },
    { emoji: '⭐', text: 'Lo más vendido', query: '¿Qué es lo más vendido?' },
    { emoji: '🦐', text: 'Mariscos', query: '¿Qué mariscos me recomiendas?' },
    { emoji: '💰', text: 'Económico', query: '¿Qué opciones económicas tienen?' }
  ];
  
  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // Entry animation - Froggy walks in
  useEffect(() => {
    if (isEntering) {
      setPose('walking');
      setTimeout(() => {
        setPose('waving');
        setIsEntering(false);
        // Show proactive bubble after entrance
        setTimeout(() => {
          const randomMessage = proactiveMessages[Math.floor(Math.random() * proactiveMessages.length)];
          setBubbleMessage(randomMessage);
          setShowBubble(true);
          // Hide bubble after 5 seconds
          setTimeout(() => setShowBubble(false), 5000);
        }, 1000);
      }, 1500);
    }
  }, []);
  
  // Floating idle animation
  useEffect(() => {
    if (!isOpen && !isEntering) {
      const interval = setInterval(() => {
        setFloatingPosition({
          x: Math.sin(Date.now() / 1000) * 3,
          y: Math.sin(Date.now() / 800) * 5
        });
      }, 50);
      return () => clearInterval(interval);
    }
  }, [isOpen, isEntering]);
  
  // Periodic proactive messages to encourage interaction
  useEffect(() => {
    if (!isOpen && !isEntering) {
      const interval = setInterval(() => {
        if (!showBubble && Math.random() > 0.7) {
          const randomMessage = proactiveMessages[Math.floor(Math.random() * proactiveMessages.length)];
          setBubbleMessage(randomMessage);
          setShowBubble(true);
          setPose('waving');
          setTimeout(() => {
            setShowBubble(false);
            setPose('standing');
          }, 4000);
        }
      }, 15000);
      return () => clearInterval(interval);
    }
  }, [isOpen, isEntering, showBubble]);
  
  // Get initial greeting when chat opens
  useEffect(() => {
    if (isOpen && !hasGreeted) {
      setPose('waving');
      fetchGreeting();
      setHasGreeted(true);
      setTimeout(() => setPose('standing'), 2000);
    }
  }, [isOpen, hasGreeted]);
  
  // Setup speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      try {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = false;
        recognitionRef.current.lang = 'es-MX';
        
        recognitionRef.current.onresult = (event) => {
          const transcript = event.results[0][0].transcript;
          setInputText(transcript);
          setIsListening(false);
          if (transcript.trim()) {
            setTimeout(() => handleSend(transcript), 500);
          }
        };
        
        recognitionRef.current.onerror = () => setIsListening(false);
        recognitionRef.current.onend = () => setIsListening(false);
      } catch (e) {
        console.error('Speech recognition setup error:', e);
      }
    }
  }, []);
  
  const fetchGreeting = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/froggy/greeting`);
      const data = await res.json();
      
      let greetingMessage = `${data.greeting} Soy Froggy, tu asesor gastronómico de Mr. Frogs 🐸\n\n${data.suggestion}`;
      
      if (data.has_promos) {
        greetingMessage += `\n\n🔥 ¡Tenemos ${data.promo_count} promociones INCREÍBLES hoy!`;
      }
      
      addBotMessage(greetingMessage);
      // Dance when greeting!
      setPose('dancing');
      setTimeout(() => setPose('standing'), 2000);
    } catch (error) {
      addBotMessage('¡Hola! Soy Froggy 🐸 ¿En qué te puedo ayudar hoy?');
    }
  };
  
  // Función para resumir texto largo
  const summarizeText = (text, maxLength = 80) => {
    // Quitar markdown y emojis
    const clean = text.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/[🐸🦐🌮🍹🔥⭐💰📋\[\]#]/g, '').trim();
    // Tomar primera oración o primeras palabras
    const firstSentence = clean.split(/[.!?]/)[0];
    if (firstSentence.length <= maxLength) return firstSentence + '...';
    return firstSentence.substring(0, maxLength) + '...';
  };

  // Función para hablar con voz de OpenAI (más natural)
  const speakWithOpenAI = async (text) => {
    try {
      setIsSpeaking(true);
      setPose('waving');
      
      const response = await fetch(`${BACKEND_URL}/api/froggy/speak`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          text: text,
          voice: 'nova' // voz femenina amigable
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const audioData = `data:audio/mp3;base64,${data.audio}`;
        const audio = new Audio(audioData);
        
        audio.onended = () => {
          setIsSpeaking(false);
          setPose('standing');
        };
        
        audio.onerror = () => {
          setIsSpeaking(false);
          setPose('standing');
          // Fallback to browser voice
          fallbackSpeak(text);
        };
        
        await audio.play();
      } else {
        // Fallback to browser voice
        fallbackSpeak(text);
      }
    } catch (error) {
      console.error('OpenAI TTS error:', error);
      fallbackSpeak(text);
    }
  };
  
  // Voz del navegador como fallback
  const fallbackSpeak = (text) => {
    if (synthRef.current) {
      const cleanText = text.replace(/[🐸🦐🌮🍹🔥⭐💰📋\[\]#*]/g, '').replace(/\*\*/g, '');
      const utterance = new SpeechSynthesisUtterance(cleanText);
      utterance.lang = 'es-MX';
      utterance.rate = 1.0;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => {
        setIsSpeaking(false);
        setPose('standing');
      };
      synthRef.current.speak(utterance);
    }
  };

  const addBotMessage = (text, speakFull = true) => {
    // Guardar mensaje corto para mostrar
    const shortVersion = summarizeText(text);
    
    setMessages(prev => [...prev, { 
      role: 'bot', 
      content: text,
      shortContent: shortVersion,
      timestamp: new Date() 
    }]);
    
    // SIEMPRE hablar si está activada la voz
    if (voiceEnabled && speakFull) {
      // Cancelar cualquier habla anterior
      synthRef.current?.cancel();
      // Usar OpenAI TTS (voz más natural)
      speakWithOpenAI(text);
    }
  };
  
  const handleSend = async (text = inputText) => {
    if (!text.trim()) return;
    
    // Add user message
    setMessages(prev => [...prev, { role: 'user', content: text, timestamp: new Date() }]);
    setInputText('');
    setIsTyping(true);
    setPose('thinking');
    setShowQuickActions(false);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/froggy/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          session_id: sessionId,
          customer_name: customerName,
          customer_phone: customerPhone,
          current_product: currentProduct,
          cart_total: cartTotal,
          cart_items: cartItems
        })
      });
      
      const data = await response.json();
      
      if (data.response) {
        // Check if response contains sale-related keywords
        const isSaleResponse = /promo|oferta|descuento|2x1|gratis|ahorra/i.test(data.response);
        const isExcitedResponse = /excelente|perfecto|genial|increíble/i.test(data.response);
        
        if (isSaleResponse) {
          setPose('jumping');
        } else if (isExcitedResponse) {
          setPose('dancing');
        }
        
        const shortVersion = summarizeText(data.response);
        
        setMessages(prev => [...prev, { 
          role: 'bot', 
          content: data.response,
          shortContent: shortVersion,
          timestamp: new Date() 
        }]);
        
        // Speak response with OpenAI voice
        if (voiceEnabled) {
          speakWithOpenAI(data.response);
        }
      }
      
    } catch (error) {
      console.error('Error:', error);
      addBotMessage('Ups, tuve un problema. ¿Puedes repetir tu pregunta?');
      setPose('thinking');
    } finally {
      setIsTyping(false);
    }
  };
  
  const toggleVoice = () => {
    if (!recognitionRef.current) {
      toast.error('Tu navegador no soporta reconocimiento de voz');
      return;
    }
    
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      recognitionRef.current.start();
      setIsListening(true);
      setPose('thinking');
      setTimeout(() => {
        if (isListening) {
          recognitionRef.current?.stop();
          setIsListening(false);
          setPose('standing');
        }
      }, 10000);
    }
  };
  
  const handleQuickAction = (query) => {
    setPose('jumping');
    setTimeout(() => handleSend(query), 300);
  };
  
  // Parse markdown and render message with buttons
  const renderMessageWithButtons = (content) => {
    let processedContent = content.replace(/\*\*([^*]+)\*\*/g, '{{BOLD}}$1{{/BOLD}}');
    const parts = processedContent.split(/(\[Agregar:\s*[^\]]+\])/gi);
    
    return parts.map((part, idx) => {
      const cartMatch = part.match(/\[Agregar:\s*([^\]]+)\]/i);
      if (cartMatch) {
        return (
          <button
            key={idx}
            onClick={() => {
              setPose('dancing');
              setTimeout(() => setPose('standing'), 1500);
              onAddToCart && onAddToCart(cartMatch[1].trim());
            }}
            className="inline-flex items-center gap-1 px-3 py-1 bg-green-500 text-white rounded-full text-sm font-medium hover:bg-green-600 transition-colors mx-1 my-1"
          >
            <ShoppingCart className="w-3 h-3" />
            Agregar {cartMatch[1].trim()}
          </button>
        );
      }
      
      const boldParts = part.split(/(\{\{BOLD\}\}[^{]+\{\{\/BOLD\}\})/g);
      return (
        <span key={idx}>
          {boldParts.map((bp, bIdx) => {
            const boldMatch = bp.match(/\{\{BOLD\}\}([^{]+)\{\{\/BOLD\}\}/);
            if (boldMatch) {
              return <strong key={bIdx} className="font-bold text-green-700">{boldMatch[1]}</strong>;
            }
            return <span key={bIdx}>{bp}</span>;
          })}
        </span>
      );
    });
  };
  
  const handleOpenChat = () => {
    setShowBubble(false);
    setPose('jumping');
    setTimeout(() => {
      setIsOpen(true);
      setPose('waving');
    }, 300);
  };

  return (
    <>
      {/* Floating Froggy Button */}
      {!isOpen && (
        <div
          className="fixed bottom-24 right-4 z-[200] cursor-pointer group"
          style={{
            transform: `translate(${floatingPosition.x}px, ${floatingPosition.y}px)`,
            transition: 'transform 0.1s ease-out'
          }}
          onClick={handleOpenChat}
          data-testid="froggy-elite-btn"
        >
          {/* Speech Bubble */}
          {showBubble && <SpeechBubble message={bubbleMessage} />}
          
          {/* Avatar */}
          <div className="relative">
            <FroggyAvatar 
              pose={pose} 
              isTyping={false} 
              isSpeaking={false}
              size="large"
            />
            {/* IA Badge */}
            <div className="absolute -top-1 -right-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs px-2 py-0.5 rounded-full font-bold animate-pulse shadow-lg">
              IA
            </div>
          </div>
          
          {/* Hover tooltip */}
          <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-white px-3 py-2 rounded-xl shadow-xl opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border-2 border-green-400">
            <span className="text-sm font-bold text-green-700">¡Haz clic! Te ayudo 🐸</span>
          </div>
        </div>
      )}
      
      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-4 right-4 z-[200] w-[400px] max-w-[calc(100vw-2rem)] h-[650px] max-h-[calc(100vh-2rem)] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden border-4 border-green-400 animate-in slide-in-from-bottom-5 duration-300"
          data-testid="froggy-elite-chat"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 via-green-400 to-emerald-500 text-white p-4 flex items-center gap-4">
            <FroggyAvatar 
              pose={pose} 
              isTyping={isTyping} 
              isSpeaking={isSpeaking}
              size="normal"
            />
            <div className="flex-1">
              <h3 className="font-bold text-lg flex items-center gap-2">
                Froggy
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">ELITE</span>
              </h3>
              <p className="text-green-100 text-sm flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                Experto en Mariscos • IA
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setVoiceEnabled(!voiceEnabled)}
                className={`p-2 rounded-full transition-all ${voiceEnabled ? 'bg-white text-green-600' : 'hover:bg-white/20'}`}
                title={voiceEnabled ? 'Silenciar' : 'Activar voz'}
              >
                {voiceEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
              </button>
              <button
                onClick={() => { setIsOpen(false); setPose('waving'); }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-green-50 to-white">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'bot' && (
                  <div className="w-10 h-10 mr-2 flex-shrink-0 rounded-full overflow-hidden border-2 border-green-500">
                    <img 
                      src={FROGGY_LOGO}
                      alt="Froggy"
                      className={`w-full h-full object-cover ${isSpeaking ? 'animate-pulse' : ''}`}
                      style={{ objectPosition: 'center 25%' }}
                    />
                  </div>
                )}
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-br-sm'
                      : 'bg-white text-gray-800 rounded-bl-sm shadow-md border border-green-200'
                  }`}
                >
                  {msg.role === 'bot' ? (
                    <div className="text-sm">
                      {/* Mostrar versión corta si está hablando, completa si no */}
                      {isSpeaking && msg === messages[messages.length - 1] ? (
                        <div className="flex items-center gap-2">
                          <span className="animate-pulse">🔊</span>
                          <span>{msg.shortContent || msg.content.substring(0, 60) + '...'}</span>
                        </div>
                      ) : (
                        <div className="whitespace-pre-wrap leading-relaxed">
                          {renderMessageWithButtons(msg.content)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap text-sm leading-relaxed">
                      {msg.content}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {isTyping && messages[messages.length - 1]?.role !== 'bot' && (
              <div className="flex justify-start items-end gap-2">
                <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-green-400 flex-shrink-0">
                  <img 
                    src={FROGGY_LOGO}
                    alt="Froggy thinking"
                    className="w-full h-full object-cover animate-pulse"
                    style={{ objectPosition: 'center 25%' }}
                  />
                </div>
                <div className="bg-white p-3 rounded-2xl rounded-bl-sm shadow-md border border-green-200">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          {/* Quick Actions - Sales focused */}
          {showQuickActions && messages.length <= 1 && (
            <div className="px-4 pb-2 flex gap-2 flex-wrap bg-white border-t border-green-100">
              <p className="w-full text-xs text-gray-500 mb-1">¿Qué te interesa?</p>
              {quickActions.map((action, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuickAction(action.query)}
                  className="px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 hover:from-green-200 hover:to-emerald-200 rounded-full text-sm font-medium text-green-700 transition-all hover:scale-105 flex items-center gap-1 shadow-sm"
                >
                  <span>{action.emoji}</span>
                  <span>{action.text}</span>
                </button>
              ))}
            </div>
          )}
          
          {/* Input */}
          <div className="p-4 border-t border-green-200 bg-white">
            <div className="flex items-center gap-2">
              <button
                onClick={toggleVoice}
                className={`p-3 rounded-full transition-all ${
                  isListening 
                    ? 'bg-red-500 text-white animate-pulse scale-110' 
                    : 'bg-green-100 text-green-600 hover:bg-green-200'
                }`}
                title={isListening ? 'Escuchando...' : 'Hablar'}
              >
                {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
              
              <input
                ref={inputRef}
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Escríbeme tu antojo..."
                className="flex-1 px-4 py-3 border-2 border-green-200 rounded-full focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent text-gray-700"
                data-testid="froggy-input"
              />
              
              <button
                onClick={() => handleSend()}
                disabled={!inputText.trim() || isTyping}
                className="p-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full hover:from-green-600 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:scale-105"
                data-testid="froggy-send"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
            
            {/* Powered by badge */}
            <p className="text-center text-xs text-gray-400 mt-2">
              🐸 Powered by Mr. Frogs AI
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default FroggyEliteAnimated;
